import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Commission,
  CommissionProfile,
  parseCm2BucketValue,
} from "../../../core/models/entities/Commission";
import { LogoOptions } from "../../../core/models/entities/LogoOptions";
import { Method } from "../../../core/models/entities/TransferOptions";
import {
  BaseLogo,
  getLogoColors,
  isDigital,
} from "../../../core/models/entities/BaseLogo";
import { copy } from "../../../utils/functions/ObjectUtil";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { SortDirection } from "../GenericTable/Models/sortDirection";
import { useErrorToast } from "../Toast/Toast";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { BadgeChip, BadgeTone } from "../OrgShell/BadgeChip";
import { CommissionPicker } from "./commission-picker.component";
import { CommissionPickerGroup } from "./commission-picker-group.component";
import {
  CommissionOptions,
  MethodCommissions,
} from "../../screens/customer-update/component/commission-editor.component";
import {
  applyCommissionAmount,
  describeCommissionRule,
  formatCommissionRate,
  resolveAdditionalFeesRule,
  resolveCommissionRule,
  resolveHeatingRule,
} from "./commission-math";
import { DialogFrame, useDialog } from "../Dialogs/dialog-base";
import { BoxHeaderTypography } from "../Typographies/BoxHeaderTypography";
import { DigitalColorElement } from "../Logo/DigitalColorElement";
import { VectorColorElement } from "../Logo/VectorColorElement";
import LoadingElementIsolated from "../loading-element/loading-element-isolated";

export interface CommissionEditorV2Props {
  userType: "vendor" | "customer";
  commissions?: CommissionProfile;
  onCommissionUpdate: (commission: CommissionProfile) => void;
  logoOptions: LogoOptions;
  options: CommissionOptions;
  /** Optional extra summary card (e.g. the profile's default VAT for new customers). */
  extraSummaryCard?: React.ReactNode;
}

/**
 * Commission editor v2: summary rate cards, per-type method table with
 * rule-source badges, and a live example whose math replicates the API's
 * PriceAdjustmentService exactly (see commission-math.ts).
 *
 * The underlying data flow is unchanged from the original CommissionEditor:
 * the same CommissionProfile shape is edited and saved through the same
 * endpoints; only the presentation is new.
 */
export function CommissionEditorV2({
  userType,
  commissions: source,
  onCommissionUpdate,
  logoOptions,
  options,
  extraSummaryCard,
}: CommissionEditorV2Props) {
  const { currency } = useProfileContext();
  const commissions: CommissionProfile = source
    ? copy(source)
    : ({ typeOnly: [], methods: [] } as unknown as CommissionProfile);
  commissions.typeOnly = commissions.typeOnly ?? [];
  commissions.methods = commissions.methods ?? [];

  const overAll: Commission =
    commissions.overAll ??
    ({ type: "OverAll", unit: "%", value: 0 } as Commission);
  const additionalFees: Commission =
    commissions.additionalFees ??
    ({ type: "AdditionalFees", unit: "%", value: 0 } as Commission);
  const heating: Commission =
    commissions.heating ??
    ({ type: "Heating", unit: "%", value: 0 } as Commission);

  const [typeSelectedIndex, setTypeSelectedIndex] = useState(0);
  const selectedType = options.types[typeSelectedIndex];

  // ----- Updates (same data flow as the original editor) -----

  function updateOverAll(commission: Commission) {
    commissions.overAll = commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateAdditionalFees(commission: Commission) {
    commissions.additionalFees =
      commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateHeating(commission: Commission) {
    commissions.heating = commission.value !== 0 ? commission : undefined;
    onCommissionUpdate(commissions);
  }

  function updateTypeOnly(commission: Commission) {
    commissions.typeOnly = commissions.typeOnly.filter(
      (to) => to.typeKey !== commission.typeKey
    );
    if (commission.value !== 0) {
      commissions.typeOnly.push(commission);
    }
    // Method rules of this type now inherit the new type value (existing flow).
    commissions.methods = commissions.methods.filter(
      (m) => m.typeKey !== commission.typeKey
    );
    onCommissionUpdate(commissions);
  }

  function updateMethod(methodCommissions: MethodCommissions) {
    const entries = [methodCommissions.base, ...methodCommissions.sizeMethods];
    for (const entry of entries) {
      commissions.methods = commissions.methods.filter(
        (m) =>
          m.methodKey !== entry.methodKey ||
          m.maxDimensions !== entry.maxDimensions
      );
      if (entry.value !== 0) {
        commissions.methods.push(entry);
      }
    }
    onCommissionUpdate(commissions);
  }

  // ----- Derived views -----

  function typeCommissionFor(typeKey: string): Commission {
    return (
      commissions.typeOnly.find((t) => t.typeKey === typeKey) ?? {
        type: "TypeOnly",
        typeKey,
        value: overAll.value,
        unit: overAll.unit,
        maxDimensions: null,
        maxSizeCM2: null,
        maxSize: null,
        methodKey: undefined,
      }
    );
  }

  /** Builds the base+sizes view the per-method picker edits (existing flow).
   *  Size rows keep the bucket label in maxDimensions for identity in the
   *  pickers; for cm2 price groups they also carry the numeric bucket in
   *  maxSizeCM2 plus the method's NAV priceType, which is what the API price
   *  engine actually matches on (the save payload clears the label). */
  function methodCommissionsFor(method: Method): MethodCommissions {
    const typeFallback = typeCommissionFor(method.type);
    const base: Commission = commissions.methods.find(
      (m) => m.methodKey === method.methodKey && !m.maxDimensions
    ) ?? {
      value: typeFallback.value,
      maxDimensions: null,
      methodKey: method.methodKey,
      typeKey: method.type,
      type: "Method",
      unit: typeFallback.unit,
      maxSize: null,
      maxSizeCM2: null,
      priceType: String(method.priceType),
    };
    const sizeMethods = method.options.map(
      (size) =>
        commissions.methods.find(
          (m) => m.methodKey === method.methodKey && m.maxDimensions === size
        ) ?? {
          value: base.value,
          maxDimensions: size,
          methodKey: method.methodKey,
          typeKey: method.type,
          type: "Method",
          unit: base.unit,
          maxSize: null,
          maxSizeCM2: parseCm2BucketValue(size),
          priceType: String(method.priceType),
        }
    );
    return { base, sizeMethods };
  }

  interface MethodRow {
    method: Method;
    effective: Commission;
    source: "Method" | "Type" | "Default" | "None";
    overriddenSizes: number;
  }

  function rowFor(method: Method): MethodRow {
    const explicit = commissions.methods.filter(
      (m) => m.methodKey === method.methodKey
    );
    const explicitBase = explicit.find((m) => !m.maxDimensions);
    const overriddenSizes = explicit.filter((m) => !!m.maxDimensions).length;
    const type = commissions.typeOnly.find((t) => t.typeKey === method.type);

    if (explicitBase || overriddenSizes > 0) {
      return {
        method,
        effective: explicitBase ?? type ?? overAll,
        source: "Method",
        overriddenSizes,
      };
    }
    if (type) {
      return { method, effective: type, source: "Type", overriddenSizes: 0 };
    }
    if (overAll.value !== 0) {
      return {
        method,
        effective: overAll,
        source: "Default",
        overriddenSizes: 0,
      };
    }
    return { method, effective: overAll, source: "None", overriddenSizes: 0 };
  }

  const methodsOfSelectedType = selectedType
    ? options.transfers
        .filter((m) => m.type === selectedType.typeKey)
        .sort((a, b) => {
          const aKey = a.methodKey?.toUpperCase() ?? "";
          const bKey = b.methodKey?.toUpperCase() ?? "";
          if (aKey === "STANDARD") return -1;
          if (bKey === "STANDARD") return 1;
          return aKey.localeCompare(bKey);
        })
    : [];

  // ----- Method override dialog -----
  const overrideDialog = useDialog();
  const [overrideMethod, setOverrideMethod] = useState<Method | undefined>();

  function openOverride(method: Method) {
    setOverrideMethod(method);
    overrideDialog.openDialog();
  }

  const sourceTone: Record<MethodRow["source"], BadgeTone> = {
    Method: "blue",
    Type: "green",
    Default: "gray",
    None: "gray",
  };

  return (
    <div className="space-y-8">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <RateCard
          title="Default commission"
          description={
            userType === "customer"
              ? "Fallback markup for this customer when no type or method rule matches."
              : "Fallback markup for all customers when no type or method rule matches."
          }
        >
          <CommissionPicker commission={overAll} onChange={updateOverAll} />
        </RateCard>
        <RateCard
          title="Extra fees"
          description="Markup on startup, film and other extra fees. No fallback to the default commission."
        >
          <CommissionPicker
            commission={additionalFees}
            onChange={updateAdditionalFees}
          />
        </RateCard>
        <RateCard
          title="Heating"
          description="Markup on the heating fee. No fallback to the default commission."
        >
          <CommissionPicker commission={heating} onChange={updateHeating} />
        </RateCard>
        {extraSummaryCard}
      </div>

      <p className="rounded-md border border-sky-100 bg-sky-50 px-4 py-2.5 text-xs text-sky-800">
        How rules apply: <span className="font-semibold">size</span> →{" "}
        <span className="font-semibold">method</span> →{" "}
        <span className="font-semibold">type</span> →{" "}
        <span className="font-semibold">default</span>. The most specific
        matching rule wins; less specific rules are ignored for that logo.
      </p>

      {/* Type tabs */}
      <div>
        <div className="flex flex-wrap gap-2 border-b pb-2">
          {options.types.map((type, index) => {
            const count = options.transfers.filter(
              (m) => m.type === type.typeKey
            ).length;
            const active = index === typeSelectedIndex;
            return (
              <button
                key={type.typeKey}
                type="button"
                onClick={() => setTypeSelectedIndex(index)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-10 font-semibold text-primary-100"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {type.nickname || type.typeKey}
                <span className="ml-1.5 text-xs text-gray-400">{count}</span>
              </button>
            );
          })}
        </div>

        {selectedType && (
          <div className="mt-4 space-y-4">
            <div className="flex items-end justify-between gap-4">
              <CommissionPicker
                labelText={`All ${
                  selectedType.nickname || selectedType.typeKey
                } methods`}
                commission={typeCommissionFor(selectedType.typeKey)}
                onChange={updateTypeOnly}
              />
              <p className="max-w-md text-xs text-gray-500">
                Setting a type rate replaces the method overrides below with
                the new inherited value.
              </p>
            </div>

            <div className="overflow-x-auto rounded border">
              <table className="w-full table-auto">
                <thead className="bg-[#FCFDFE] text-left">
                  <tr>
                    <th className="py-3 pl-4 text-xs font-medium text-gray-600">
                      Method
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-600">
                      Customer pays
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-600">
                      Rule source
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-600">
                      Sizes
                    </th>
                    <th className="w-24 px-4 py-3 text-xs font-medium text-gray-600" />
                  </tr>
                </thead>
                <tbody>
                  {methodsOfSelectedType.map((method) => {
                    const row = rowFor(method);
                    return (
                      <tr
                        key={method.methodKey}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="py-2 pl-4 text-xs capitalize">
                          {method.displayName?.toLowerCase() ||
                            method.methodKey}
                        </td>
                        <td className="px-4 py-2 text-xs font-semibold text-gray-700">
                          {row.source === "None"
                            ? "Base price"
                            : `Base ${formatCommissionRate(
                                row.effective,
                                currency
                              )}`}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <BadgeChip tone={sourceTone[row.source]}>
                            {row.source === "None" ? "No rule" : row.source}
                          </BadgeChip>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {method.options.length}{" "}
                          {method.options.length === 1 ? "bucket" : "buckets"}
                          {row.overriddenSizes > 0 &&
                            ` · ${row.overriddenSizes} custom`}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <SecondaryButton onClick={() => openOverride(method)}>
                            Override
                          </SecondaryButton>
                        </td>
                      </tr>
                    );
                  })}
                  {methodsOfSelectedType.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-xs text-gray-500"
                      >
                        No methods for this type
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Method override dialog (existing per-method picker) */}
      {overrideMethod && (
        <DialogFrame
          dialogOption={overrideDialog}
          onClose={() => setOverrideMethod(undefined)}
        >
          <div className="min-h-[24rem] space-y-4">
            <BoxHeaderTypography>
              Override —{" "}
              {overrideMethod.displayName?.toLowerCase() ||
                overrideMethod.methodKey}
            </BoxHeaderTypography>
            <p className="max-w-sm text-xs text-gray-500">
              The base rate applies to every size of this method. Use EDIT
              SIZES to give individual size buckets their own rate. Setting a
              rate to 0 removes the override so the type or default rule
              applies again.
            </p>
            <CommissionPickerGroup
              key={overrideMethod.methodKey}
              labelText={
                overrideMethod.displayName?.toLowerCase() ||
                overrideMethod.methodKey
              }
              method={overrideMethod}
              commissions={methodCommissionsFor(overrideMethod)}
              updateCommission={updateMethod}
            />
          </div>
        </DialogFrame>
      )}

      {/* Live example v2 */}
      <LiveExampleV2
        commissions={commissions}
        transfers={options.transfers}
        logoOptions={logoOptions}
      />
    </div>
  );
}

export function RateCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-md border bg-gray-150 p-4">
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
      <p className="mb-3 mt-0.5 flex-1 text-xs text-gray-500">{description}</p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live example v2
// ---------------------------------------------------------------------------

interface LiveExampleV2Props {
  commissions: CommissionProfile;
  transfers: Method[];
  logoOptions: LogoOptions;
}

function LiveExampleV2({
  commissions,
  transfers,
  logoOptions,
}: LiveExampleV2Props) {
  const { currency } = useProfileContext();
  const { logoRepository } = useRepositoriesContext();

  // ----- Example-logo search (same flow as the original live example) -----
  const [skuSearch, setSkuSearch] = useState("");
  const [activeSkuSearch, setActiveSkuSearch] = useState("");
  const [displayedLogo, setDisplayedLogo] = useState<BaseLogo | null>(null);
  const previousSearchRef = useRef("");

  // Classification 3 = "Logo" (ImageLogo/static logos) - a data value, not UI copy.
  const { data: logoData, isLoading: isLoadingLogo } =
    logoRepository.useAllLogos({
      searchTerm: activeSkuSearch,
      filterBy: [{ field: "classification", value: "3" }],
      sortBy: { field: "createdAtUtc", sortDirection: SortDirection.DESC },
      page: { size: 1, index: 1 },
    });

  const fetchedLogo = logoData?.results?.[0];

  useEffect(() => {
    if (isLoadingLogo) return;
    if (fetchedLogo) {
      setDisplayedLogo(fetchedLogo);
      previousSearchRef.current = activeSkuSearch;
    } else if (
      activeSkuSearch &&
      activeSkuSearch !== previousSearchRef.current
    ) {
      useErrorToast(`Logo with Item No. "${activeSkuSearch}" not found`);
      setActiveSkuSearch(previousSearchRef.current);
    }
  }, [fetchedLogo, isLoadingLogo, activeSkuSearch]);

  function handleSkuSearch() {
    if (skuSearch.trim()) {
      setActiveSkuSearch(skuSearch.trim());
      setSkuSearch("");
    }
  }

  const exampleLogo = displayedLogo;

  // ----- API-replica rule resolution -----
  const rule = useMemo(
    () => resolveCommissionRule(commissions, exampleLogo, transfers),
    [commissions, exampleLogo, transfers]
  );
  const feesRule = resolveAdditionalFeesRule(commissions);
  const heatingRule = resolveHeatingRule(commissions);

  const prices = useMemo(
    () =>
      [...(exampleLogo?.product?.prices ?? [])].sort(
        (a, b) => a.minQuantity - b.minQuantity
      ),
    [exampleLogo]
  );

  const fees = exampleLogo?.product?.additionalFees ?? [];

  // Base heating value depends on currency (DKK uses the higher value) -
  // mirrors the original live example.
  const baseHeatingValue = currency === "DKK" ? 9.25 : 1.25;

  const fullPreviewDialog = useDialog();

  const fmt = (value: number) => `${currency} ${value.toFixed(2)}`;

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h4 className="flex-1 text-sm font-semibold text-gray-700">
          Live example
        </h4>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Enter Item No..."
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSkuSearch()}
            className="w-32 rounded border border-borderGray px-2 py-1 text-xs placeholder:italic focus:border-gray-400 focus:outline-none"
          />
          <button
            onClick={handleSkuSearch}
            className="rounded border border-borderGray bg-slate-100 px-2 py-1 text-xs hover:bg-slate-200"
          >
            Search
          </button>
          <SecondaryButton
            onClick={fullPreviewDialog.openDialog}
            disabled={!exampleLogo}
          >
            Open full live preview
          </SecondaryButton>
        </div>
      </div>

      {!exampleLogo && !isLoadingLogo ? (
        <p className="py-6 text-center text-xs italic text-gray-400">
          No example logo available
        </p>
      ) : (
        <div className="relative">
          {isLoadingLogo && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/80">
              <LoadingElementIsolated />
            </div>
          )}

          <div className="flex flex-wrap items-start gap-6">
            <div className="min-w-[16rem] flex-1 space-y-4">
              <div className="flex items-center gap-3 rounded-md border bg-gray-150 p-3">
                <img
                  src={
                    exampleLogo?.displayImage?.highResUrl ||
                    exampleLogo?.displayImage?.lowResUrl
                  }
                  alt={exampleLogo?.title || "Example logo"}
                  className="h-14 w-14 rounded bg-white object-contain p-1"
                />
                <div className="min-w-0 text-xs text-gray-600">
                  <p className="truncate font-semibold">
                    {exampleLogo?.title}
                    {exampleLogo?.product?.sku && (
                      <span className="font-normal text-gray-400">
                        {" "}
                        ({exampleLogo.product.sku})
                      </span>
                    )}
                  </p>
                  <p className="capitalize">
                    {exampleLogo?.transferMethod?.type?.toLowerCase()} ·{" "}
                    {exampleLogo?.transferMethod?.displayName?.toLowerCase()} ·{" "}
                    {exampleLogo?.heightMm}×{exampleLogo?.widthMm} mm
                  </p>
                  <p className="mt-1 text-sky-700">
                    Rule applied: {describeCommissionRule(rule, transfers)}
                    {rule.source !== "none" &&
                      ` (${formatCommissionRate(rule.commission, currency)})`}
                  </p>
                </div>
              </div>

              {/* Fee rows with the rate that hit them */}
              <table className="w-full table-auto">
                <tbody>
                  {fees.map((fee) => {
                    const feeTier = [...(fee.prices ?? [])].sort(
                      (a, b) => a.minQuantity - b.minQuantity
                    )[0];
                    const yourFee = feeTier?.adjustedPrice ?? 0;
                    return (
                      <tr key={fee.name} className="border-b">
                        <td className="py-1.5 text-xs text-gray-600">
                          {fee.name}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs text-gray-500">
                          {fmt(yourFee)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700">
                          {fmt(
                            applyCommissionAmount(yourFee, feesRule.commission)
                          )}
                        </td>
                        <td className="py-1.5 text-right text-xs text-gray-400">
                          {feesRule.source === "none"
                            ? "no markup"
                            : `Extra fees ${formatCommissionRate(
                                feesRule.commission,
                                currency
                              )}`}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td className="py-1.5 text-xs text-gray-600">Heating</td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-500">
                      {fmt(baseHeatingValue)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700">
                      {fmt(
                        applyCommissionAmount(
                          baseHeatingValue,
                          heatingRule.commission
                        )
                      )}
                    </td>
                    <td className="py-1.5 text-right text-xs text-gray-400">
                      {heatingRule.source === "none"
                        ? "no markup"
                        : `Heating ${formatCommissionRate(
                            heatingRule.commission,
                            currency
                          )}`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Quantity tier table */}
            <div className="min-w-[18rem] flex-1">
              <table className="w-full table-auto">
                <thead className="text-left">
                  <tr className="border-b">
                    <th className="py-2 text-xs font-medium text-gray-600">
                      Qty
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-600">
                      Your price/pc
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-600">
                      Customer/pc
                    </th>
                    <th className="py-2 text-right text-xs font-medium text-gray-600">
                      Margin/pc
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((price, idx) => {
                    const customer = applyCommissionAmount(
                      price.adjustedPrice,
                      rule.commission
                    );
                    return (
                      <tr key={idx} className="border-b">
                        <td className="py-1.5 text-xs text-gray-600">
                          {price.minQuantity || 1}+
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs text-gray-500">
                          {fmt(price.adjustedPrice)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700">
                          {fmt(customer)}
                        </td>
                        <td className="py-1.5 text-right text-xs text-emerald-700">
                          {fmt(customer - price.adjustedPrice)}
                        </td>
                      </tr>
                    );
                  })}
                  {prices.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-4 text-center text-xs italic text-gray-400"
                      >
                        No prices set
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Full live preview (both price sets, all buckets) */}
      <DialogFrame dialogOption={fullPreviewDialog}>
        <FullLivePreview
          logo={exampleLogo}
          logoOptions={logoOptions}
          commissions={commissions}
          transfers={transfers}
          baseHeatingValue={baseHeatingValue}
        />
      </DialogFrame>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full live preview panel (the complete your-prices vs customer-prices view)
// ---------------------------------------------------------------------------

function FullLivePreview({
  logo,
  logoOptions,
  commissions,
  transfers,
  baseHeatingValue,
}: {
  logo: BaseLogo | null;
  logoOptions: LogoOptions;
  commissions: CommissionProfile;
  transfers: Method[];
  baseHeatingValue: number;
}) {
  const { currency } = useProfileContext();

  if (!logo) {
    return (
      <p className="py-6 text-center text-xs italic text-gray-400">
        No example logo available
      </p>
    );
  }

  const rule = resolveCommissionRule(commissions, logo, transfers);
  const feesRule = resolveAdditionalFeesRule(commissions);
  const heatingRule = resolveHeatingRule(commissions);

  const colors = getLogoColors(
    logo,
    logoOptions.colors,
    logoOptions.colorsEmbroidery
  );
  const colorsElements = isDigital(logo) ? (
    <DigitalColorElement />
  ) : (
    <div className="flex">
      {colors.map((co) =>
        co.isIndigo ? (
          <DigitalColorElement key={co.id} showText={false} text={co.name} />
        ) : (
          <VectorColorElement key={co.id} showName={false} color={co} />
        )
      )}
    </div>
  );

  const prices = [...(logo.product?.prices ?? [])].sort(
    (a, b) => a.minQuantity - b.minQuantity
  );
  const fees = logo.product?.additionalFees ?? [];
  const fmt = (value: number) => `${currency} ${value.toFixed(2)}`;

  function priceColumn(customerSide: boolean) {
    return (
      <div className="flex flex-row space-x-8">
        <div className="space-y-1">
          <p className="text-xs text-gray-500">Prices</p>
          {prices.map((price, idx) => (
            <p key={idx} className="text-xs text-gray-400">
              {fmt(
                customerSide
                  ? applyCommissionAmount(price.adjustedPrice, rule.commission)
                  : price.adjustedPrice
              )}{" "}
              ({price.minQuantity || 1})
            </p>
          ))}
          {prices.length === 0 && (
            <p className="text-xs italic text-gray-400">No prices set</p>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-xs text-gray-500">Extra Fees</p>
          {fees.map((fee, feeIdx) => (
            <div key={feeIdx}>
              <p className="text-xs text-gray-400">{fee.name}</p>
              {[...(fee.prices ?? [])]
                .sort((a, b) => a.minQuantity - b.minQuantity)
                .map((price, priceIdx) => (
                  <p key={priceIdx} className="text-xs text-gray-400">
                    {fmt(
                      customerSide
                        ? applyCommissionAmount(
                            price.adjustedPrice,
                            feesRule.commission
                          )
                        : price.adjustedPrice
                    )}{" "}
                    ({price.minQuantity}+ pcs)
                  </p>
                ))}
            </div>
          ))}
          {fees.length === 0 && (
            <p className="text-xs italic text-gray-400">No extra fees</p>
          )}
          <p className="mt-2 text-xs text-gray-500">Heating</p>
          <p className="text-xs text-gray-400">
            {fmt(
              customerSide
                ? applyCommissionAmount(baseHeatingValue, heatingRule.commission)
                : baseHeatingValue
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-[42rem] space-y-4">
      <BoxHeaderTypography>Full live preview</BoxHeaderTypography>
      <div className="flex items-center gap-4 rounded-md border bg-gray-150 p-3">
        <img
          src={logo.displayImage?.highResUrl || logo.displayImage?.lowResUrl}
          alt={logo.title || "Example logo"}
          className="h-16 w-16 rounded bg-white object-contain p-1"
        />
        <div className="text-xs text-gray-600">
          <p className="font-semibold">
            {logo.title}
            {logo.product?.sku && (
              <span className="font-normal text-gray-400">
                {" "}
                ({logo.product.sku})
              </span>
            )}
          </p>
          <p className="capitalize">
            {logo.transferMethod?.type?.toLowerCase()} ·{" "}
            {logo.transferMethod?.displayName?.toLowerCase()} · {logo.heightMm}×
            {logo.widthMm} mm
          </p>
          <div className="mt-1 flex items-center">
            <span className="pr-1 font-bold">Colors</span>
            {colorsElements}
          </div>
          <p className="mt-1 text-sky-700">
            Rule applied: {describeCommissionRule(rule, transfers)}
            {rule.source !== "none" &&
              ` (${formatCommissionRate(rule.commission, currency)})`}
          </p>
        </div>
      </div>
      <div className="flex items-start justify-between gap-8 p-2">
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-600">
            Your Prices
          </p>
          {priceColumn(false)}
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-600">
            Customer Prices
          </p>
          {priceColumn(true)}
        </div>
      </div>
    </div>
  );
}
